<template>
  <div class="settings-container">
    <div class="settings-card">
      <div class="settings-header">
        <div class="settings-title-wrapper">
          <div class="settings-icon">
            <i class="pi pi-cog"></i>
          </div>
          <div class="settings-text">
            <h2 class="settings-title">Настройки</h2>
            <p class="settings-subtitle">Управление параметрами системы</p>
          </div>
        </div>
      </div>

      <TabView class="settings-tabs">
        <!-- Tab: Синхронизация -->
        <TabPanel>
          <template #header>
            <i class="pi pi-sync mr-2"></i>
            <span>Синхронизация</span>
          </template>
          <div class="tab-content">
            <div class="settings-section">
              <div class="section-header">
                <i class="pi pi-clock"></i>
                <h3>Периоды опроса</h3>
              </div>
              <div class="formgrid grid mt-3">
                <div class="field col-12 md:col-4">
                  <label for="update_monitoring" class="field-label">
                    <i class="pi pi-eye mr-2"></i>Период опроса мониторинга, сек
                  </label>
                  <InputNumber id="update_monitoring" v-model="setting.monitoringUpdate" mode="decimal" showButtons 
                    :min="14" :max="120" :step="2" inputClass="w-full" 
                    @update:modelValue="changeMonitoringUpdate" />
                  <small class="field-hint">Интервал от 14 до 120 секунд</small>
                </div>
                <div class="field col-12 md:col-4">
                  <label for="update_property" class="field-label">
                    <i class="pi pi-bell mr-2"></i>Период опроса событий/свойств, сек
                  </label>
                  <InputNumber id="update_property" v-model="setting.propertyUpdate" mode="decimal" showButtons 
                    :min="10" :max="100" :step="2" inputClass="w-full" 
                    @update:modelValue="changePropertyUpdate" />
                  <small class="field-hint">Интервал от 10 до 100 секунд</small>
                </div>
                <div class="field col-12 md:col-4">
                  <label for="update_event" class="field-label">
                    <i class="pi pi-bell-fill mr-2"></i>Период обновления событий, сек
                  </label>
                  <InputNumber id="update_event" v-model="setting.eventUpdate" mode="decimal" showButtons 
                    :min="120" :max="6000" :step="2" inputClass="w-full" 
                    @update:modelValue="changeEventUpdate" />
                  <small class="field-hint">Интервал от 120 до 6000 секунд</small>
                </div>
              </div>
            </div>
          </div>
        </TabPanel>

        <!-- Tab: Уведомления -->
        <TabPanel>
          <template #header>
            <i class="pi pi-bell-fill mr-2"></i>
            <span>Уведомления</span>
          </template>
          <div class="tab-content">
            <div class="settings-section">
              <div class="section-header">
                <i class="pi pi-volume-up"></i>
                <h3>Звуковые уведомления</h3>
              </div>
              <div class="notifications-grid">
                <div class="notification-item">
                  <div class="notification-checkbox">
                    <Checkbox v-model="setting.enableEventSound" binary :inputId="'enableEventSound'" 
                      @change="changeEnableEventSound" class="mr-3" />
                  </div>
                  <div class="notification-content">
                    <label class="notification-label" for="enableEventSound">
                      Звуковое оповещение
                    </label>
                    <small class="notification-hint">
                      Включает звуковое оповещение событий
                    </small>
                  </div>
                </div>

                <div class="notification-item">
                  <div class="notification-checkbox">
                    <Checkbox v-model="setting.enableEventFacilitySound" binary :inputId="'enableEventFacilitySound'" 
                      @change="changeEnableEventFacilitySound" class="mr-3" />
                  </div>
                  <div class="notification-content">
                    <label class="notification-label" for="enableEventFacilitySound">
                      Оповещение по объектам
                    </label>
                    <small class="notification-hint">
                      Включает наименование объекта в оповещение
                    </small>
                  </div>
                </div>

                <div class="slider-group mt-4">
                  <div class="slider-item">
                    <div class="slider-header">
                      <label class="slider-label">
                        <i class="pi pi-volume-down mr-2"></i>Скорость речи
                      </label>
                      <Tag :value="setting.speechRate.toFixed(1)" severity="info" />
                    </div>
                    <Slider v-model="setting.speechRate" :inputId="'speechRate'" 
                      @change="changeSpeechRate" :min="0.1" :max="10" :step="0.1" 
                      :disabled="!setting.enableEventSound" />
                  </div>
                  <div class="slider-item">
                    <div class="slider-header">
                      <label class="slider-label">
                        <i class="pi pi-music mr-2"></i>Высота тона
                      </label>
                      <Tag :value="setting.speechPitch.toFixed(1)" severity="success" />
                    </div>
                    <Slider v-model="setting.speechPitch" :inputId="'speechPitch'" 
                      @change="changeSpeechPitch" :min="0" :max="2" :step="0.1" 
                      :disabled="!setting.enableEventSound" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabPanel>

        <!-- Tab: Общие -->
        <TabPanel>
          <template #header>
            <i class="pi pi-cog mr-2"></i>
            <span>Общие</span>
          </template>
          <div class="tab-content">
            <div class="settings-section">
              <div class="section-header">
                <i class="pi pi-list"></i>
                <h3>Общие параметры</h3>
              </div>
              <div class="general-options">
                <div class="option-item">
                  <div class="option-checkbox">
                    <Checkbox v-model="setting.hideRepeatWell" binary :inputId="'hideRepeatWell'" 
                      @change="changeHideRepeatWell" class="mr-3" />
                  </div>
                  <div class="option-content">
                    <label class="option-label" for="hideRepeatWell">
                      Скрывать повторы скважин
                    </label>
                    <small class="option-hint">
                      Включает фильтрацию дублирующихся скважин для удобного просмотра
                    </small>
                  </div>
                </div>

                <div class="option-item mt-3">
                  <div class="option-content">
                    <label class="option-label">
                      <i class="pi pi-sort mr-2"></i>Вид сортировки объектов
                    </label>
                    <SelectButton v-model="setting.structMode" :options="modeOptions" 
                      optionLabel="label" optionValue="value" @update:modelValue="changeStructMode" 
                      class="mt-2" />
                    <small class="option-hint mt-2">
                      {{ modeOptions.find(m => m.value === setting.structMode)?.hint || '' }}
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabPanel>

        <!-- Tab: Подключение -->
        <TabPanel>
          <template #header>
            <i class="pi pi-plug mr-2"></i>
            <span>Подключение</span>
          </template>
          <div class="tab-content">
            <div class="settings-section">
              <div class="section-header">
                <i class="pi pi-info-circle"></i>
                <h3>Информация о подключении</h3>
              </div>
              <div class="connection-grid">
                <div class="connection-card">
                  <div class="connection-icon">
                    <i class="pi pi-user"></i>
                  </div>
                  <div class="connection-info">
                    <span class="connection-label">Логин пользователя</span>
                    <Tag :value="login" severity="info" class="mt-1" />
                  </div>
                </div>

                <div class="connection-card">
                  <div class="connection-icon">
                    <i class="pi pi-server"></i>
                  </div>
                  <div class="connection-info">
                    <span class="connection-label">Конфигурация сервера</span>
                    <Tag :value="VUE_APP_SERVER_NAME" severity="success" class="mt-1" />
                  </div>
                </div>

                <div class="connection-card">
                  <div class="connection-icon">
                    <i class="pi pi-compass"></i>
                  </div>
                  <div class="connection-info">
                    <span class="connection-label">Профиль подключения</span>
                    <Tag :value="direction" severity="warning" class="mt-1" />
                  </div>
                </div>

                <div class="connection-card">
                  <div class="connection-icon">
                    <i class="pi pi-database"></i>
                  </div>
                  <div class="connection-info">
                    <span class="connection-label">Сервер REST API</span>
                    <a target="_blank" :href="serverUrl" class="connection-link mt-1">
                      {{ serverUrl }}
                    </a>
                  </div>
                </div>

                <div class="connection-card">
                  <div class="connection-icon">
                    <i class="pi pi-file"></i>
                  </div>
                  <div class="connection-info">
                    <span class="connection-label">Адаптер отчетов</span>
                    <a target="_blank" :href="adapterUrl" class="connection-link mt-1">
                      {{ adapterUrl }}
                    </a>
                  </div>
                </div>

                <div class="connection-card">
                  <div class="connection-icon">
                    <i class="pi pi-folder-open"></i>
                  </div>
                  <div class="connection-info">
                    <span class="connection-label">Подключение для отчетов</span>
                    <span class="connection-path mt-1" :title="connectionPath">{{ connectionPath }}</span>
                  </div>
                </div>

                <div class="connection-card">
                  <div class="connection-icon">
                    <i class="pi pi-directions-alt"></i>
                  </div>
                  <div class="connection-info">
                    <span class="connection-label">Средний слой</span>
                    <a target="_blank" :href="middleUrl" class="connection-link mt-1">
                      {{ middleUrl }}
                    </a>
                  </div>
                </div>

                <div class="connection-card">
                  <div class="connection-icon">
                    <i class="pi pi-key"></i>
                  </div>
                  <div class="connection-info">
                    <span class="connection-label">До смены пароля, дн.</span>
                    <Tag :value="passwordExpirationDay !== null ? `${passwordExpirationDay} дн.` : 'Неизвестно'" 
                      :severity="tagExpirationDay" class="mt-1" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabPanel>
      </TabView>

      <div class="settings-footer">
        <Button icon="pi pi-sitemap" label="Диагностика" severity="warning" @click="showDiagnostic = true"  />
      </div>
    </div>
  </div>

  <Diagnostic v-model:showDiagnostic="showDiagnostic" />
</template>

<script setup>
import { ref, reactive, computed } from 'vue';
import { useStore } from 'vuex';
import Diagnostic from './Diagnostic.vue';

const store = useStore();

const VUE_APP_SERVER_NAME = ref(process.env.VUE_APP_SERVER_NAME || '');

const setting = reactive({
  monitoringUpdate: store.getters.monitoringUpdate,
  propertyUpdate: store.getters.propertyUpdate,
  eventUpdate: store.getters.eventUpdate,
  hideRepeatWell: store.getters.hideRepeatWell,
  structMode: store.getters.structMode,
  enableEventSound: store.getters.enableEventSound,
  enableEventFacilitySound: store.getters.enableEventFacilitySound,
  speechRate: store.getters.speechRate,
  speechPitch: store.getters.speechPitch,
});

const showDiagnostic = ref(false);
const modeOptions = [
  { label: 'По позиции', value: 1, hint: 'По кустам' },
  { label: 'По типу(тест)', value: 2, hint: 'По скважинам' },
  { label: 'По алфавиту', value: 3, hint: '' },
];
const direction = computed(() => store.getters.direction);
const serverUrl = computed(() => store.getters.server('SERVER'));
const adapterUrl = computed(() => store.getters.server('ADAPTER'));
const middleUrl = computed(() => store.getters.server('TM_MIDDLE'));
const connectionPath = computed(() => store.getters.connectionPath);
const info = computed(() => store.getters.info);
const login = computed(() => info.value?.login || '');

const passwordExpirationDay = computed(() => {
  if (!info.value?.passwordExpirationDate) return null;
  const now = new Date();
  const exp = new Date(info.value.passwordExpirationDate);
  const diff = Math.abs(now - exp);
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
});

const tagExpirationDay = computed(() => {
  const days = passwordExpirationDay.value;
  if (days === null) return 'info';
  if (days >= 20 && days < 50) return 'warning';
  if (days < 20) return 'danger';
  return 'info';
})

const changeEventUpdate = (value) => {
  store.dispatch('setEventUpdate', value);
}
const changePropertyUpdate = (value) => {
  store.dispatch('setPropertyUpdate', value);
}
const changeMonitoringUpdate = (value) => {
  store.dispatch('setMonitoringUpdate', value);
}
const changeHideRepeatWell = () => {
  store.dispatch('setHideRepeatWell', setting.hideRepeatWell);
}
const changeStructMode = (e) => {
  store.dispatch('setStructMode', e);
}
const changeEnableEventSound = () => {
  store.dispatch('setEnableEventSound', setting.enableEventSound);
}
const changeEnableEventFacilitySound = () => {
  store.dispatch('setEnableEventFacilitySound', setting.enableEventFacilitySound);
}
const changeSpeechRate = () => {
  console.log(setting.speechRate);

  store.dispatch('setSpeechRate', setting.speechRate);
}
const changeSpeechPitch = () => {
  store.dispatch('setSpeechPitch', setting.speechPitch);
}

</script>

<style lang="scss" scoped>
.settings-container {
  padding: 1.2rem;
}

.settings-card {
  background: var(--surface-card);
  border-radius: var(--border-radius-xl);
  padding: 2rem;
  box-shadow: var(--card-shadow);
  border: 1px solid var(--surface-border);
  max-width: 1600px;
  margin: 0 auto;
}

.settings-header {
  margin-bottom: 2rem;
}

.settings-title-wrapper {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.settings-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background:  var(--primary-color);
  border-radius: 12px;
  color: white;
  font-size: 1.5rem;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.settings-text {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.settings-title {
  color: var(--surface-900);
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
}

.settings-subtitle {
  color: var(--text-color-secondary);
  font-size: 0.875rem;
  margin: 0;
}

.settings-tabs {
  :deep(.p-tabview-nav) {
    border: none;
    background: var(--surface-ground);
    border-radius: 12px;
    padding: 0.5rem;
    margin-bottom: 1.5rem;
  }

  :deep(.p-tabview-nav-link) {
    border: none;
    background: transparent;
    color: var(--text-color-secondary);
    font-weight: 600;
    padding: 0.75rem 1.25rem;
    border-radius: 8px;
    transition: all 0.2s ease;

    &:hover {
      background: var(--surface-hover);
      color: var(--text-color);
    }
  }

  :deep(.p-tabview-nav-item.p-tabview-nav-item-selected .p-tabview-nav-link) {
    background: white;
    color: #667eea;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  :deep(.p-tabview-panels) {
    padding: 0;
  }
}

.tab-content {
  padding: 1rem 0;
}

.settings-section {
  padding: 1.5rem;
  background: var(--surface-ground);
  border-radius: var(--border-radius);
  border: 1px solid var(--surface-border);
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.section-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid var(--surface-border);

  i {
    font-size: 1.25rem;
    color: #667eea;
  }

  h3 {
    color: var(--surface-700);
    font-size: 1.125rem;
    font-weight: 600;
    margin: 0;
  }
}

.field {
  margin-bottom: 1.5rem;
}

.field-label {
  display: flex;
  align-items: center;
  color: var(--surface-700);
  font-weight: 500;
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
}

.field-hint {
  color: var(--surface-500);
  font-size: 0.75rem;
  margin-top: 0.25rem;
  display: block;
}

/* Notifications Tab */
.notifications-grid {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.notification-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  background: white;
  border-radius: 10px;
  border: 1px solid var(--surface-border);
  transition: all 0.2s ease;

  &:hover {
    border-color: #667eea;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1);
  }
}

.notification-checkbox {
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.notification-content {
  flex: 1;
}

.notification-label {
  display: block;
  color: var(--surface-700);
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.notification-hint {
  color: var(--surface-500);
  font-size: 0.8rem;
  display: block;
}

.slider-group {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid var(--surface-border);
}

.slider-item {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.slider-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.slider-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--surface-700);
  font-weight: 600;
  font-size: 0.9rem;
}

/* General Tab */
.general-options {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.option-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  background: white;
  border-radius: 10px;
  border: 1px solid var(--surface-border);
  transition: all 0.2s ease;

  &:hover {
    border-color: #667eea;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1);
  }
}

.option-checkbox {
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.option-content {
  flex: 1;
}

.option-label {
  display: block;
  color: var(--surface-700);
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.option-hint {
  color: var(--surface-500);
  font-size: 0.8rem;
  display: block;
}

/* Connection Tab */
.connection-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1rem;
}

.connection-card {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.25rem;
  background: white;
  border-radius: 12px;
  border: 1px solid var(--surface-border);
  transition: all 0.2s ease;

  &:hover {
    border-color: #667eea;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.12);
    transform: translateY(-2px);
  }
}

.connection-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1));
  border-radius: 10px;
  color: #667eea;
  font-size: 1.125rem;
  flex-shrink: 0;
}

.connection-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.connection-label {
  color: var(--surface-500);
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.connection-link {
  color: #667eea;
  font-weight: 600;
  font-size: 0.9rem;
  word-break: break-all;
  transition: color 0.2s ease;

  &:hover {
    color: #764ba2;
    text-decoration: underline;
  }
}

.connection-path {
  color: var(--surface-700);
  font-weight: 500;
  font-size: 0.9rem;
  word-break: break-all;
}

.settings-footer {
  border-top: 1px solid var(--surface-border);
  padding-top: 1.5rem;
  margin-top: 2rem;
  text-align: center;
}

/* Responsive */
@media (max-width: 768px) {
  .settings-container {
    padding: 1rem;
  }

  .settings-card {
    padding: 1.5rem;
  }

  .settings-title-wrapper {
    flex-direction: column;
    align-items: flex-start;
  }

  .settings-icon {
    width: 40px;
    height: 40px;
    font-size: 1.25rem;
  }

  .settings-title {
    font-size: 1.5rem;
  }

  .connection-grid {
    grid-template-columns: 1fr;
  }

  .slider-group {
    grid-template-columns: 1fr;
  }

  :deep(.p-tabview-nav) {
    overflow-x: auto;
    white-space: nowrap;
  }
}

@media (max-width: 480px) {
  .settings-card {
    padding: 1rem;
  }

  .settings-title {
    font-size: 1.25rem;
  }

  .section-header h3 {
    font-size: 1rem;
  }

  .settings-section {
    padding: 1rem;
  }
}
</style>